import type { SceneEffect, Suggest, ToolCall, ToolFn } from './types';

export function traceLine(call: ToolCall): string {
  return `${call.tool}(${compact(call.input)}) → ${summarise(call.output)}`;
}

export const compact = (input: Record<string, unknown>) =>
  Object.entries(input)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}=${Array.isArray(v) ? `[${v.length}]` : String(v)}`)
    .join(', ');

export const summarise = (out: unknown): string => {
  if (out && typeof out === 'object') {
    const o = out as Record<string, unknown>;
    if (o.bytes instanceof Uint8Array) return `${o.bytes.length} bytes`;
    if (o.attachment && typeof o.attachment === 'object') {
      const a = o.attachment as { name: string; size: number };
      return `${a.name}, ${a.size} bytes`;
    }
    if (Array.isArray(o.created))
      return `built ${o.created.length}, removed ${Array.isArray(o.removed) ? o.removed.length : 0}`;
    if ('total' in o && 'unit' in o)
      return `${o.total}${o.unit ? ' ' + o.unit : ''} (${o.elements} el, ${o.missing} missing)`;
    if ('effect' in o && o.effect)
      return `scene ${(o.effect as SceneEffect).kind}${'count' in o ? ` (${o.count} el)` : ''}`;
    if ('issues' in o)
      return `${o.elements} el, ${Object.entries(o.issues as Record<string, number>)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${k} ${n}`)
        .join(', ')}`;
    if ('aggregate' in o && o.aggregate && typeof o.aggregate === 'object') {
      const a = o.aggregate as { op: string; field: string; value: unknown };
      return `${o.count} el, ${a.op}(${a.field}) = ${a.value}`;
    }
    if ('groups' in o && o.groups)
      return `${o.count} el in ${Object.keys(o.groups as object).length} groups`;
    if ('types' in o && 'storeys' in o)
      return `${o.elements} el, ${(o.types as unknown[]).length} types`;
    if ('count' in o) return `${o.count}`;
    if ('ids' in o && Array.isArray(o.ids)) return `${o.ids.length} ids`;
    if ('error' in o) return String(o.error);
    return 'ok';
  }
  return String(out);
};

export async function callTool(
  tools: Record<string, ToolFn>,
  name: string,
  input: Record<string, unknown>,
  calls: ToolCall[],
): Promise<unknown> {
  const fn = tools[name];
  const output = fn ? await fn(input) : { error: `unknown tool ${name}` };
  calls.push({ tool: name, input, output });
  return output;
}

export function effectsFrom(calls: ToolCall[]): SceneEffect[] {
  const out: SceneEffect[] = [];
  for (const c of calls) {
    const e = (c.output as { effect?: SceneEffect } | null)?.effect;
    if (e) out.push(e);
  }
  return out;
}

// The first outstanding fix, from the most recent call that carried findings (build or
// check_building). Read again after each fix in a "yes" chain to find the next one.
export function suggestFrom(calls: ToolCall[]): Suggest | undefined {
  for (let i = calls.length - 1; i >= 0; i--) {
    const findings = (calls[i]!.output as { findings?: { suggest?: Suggest }[] } | null)?.findings;
    if (!Array.isArray(findings)) continue;
    // stop at the most recent findings, even with nothing left to fix: an earlier call's
    // suggest is stale once a later one has re-checked the building
    return findings.find((f) => f.suggest)?.suggest;
  }
  return undefined;
}

export function highlightsFrom(calls: ToolCall[]): number[] {
  for (let i = calls.length - 1; i >= 0; i--) {
    const o = calls[i]!.output as { ids?: unknown };
    if (calls[i]!.tool === 'highlight' && Array.isArray(o.ids)) return o.ids as number[];
  }
  for (let i = calls.length - 1; i >= 0; i--) {
    const o = calls[i]!.output as { ids?: unknown };
    if (Array.isArray(o.ids)) return (o.ids as number[]).slice(0, 400);
  }
  return [];
}
